import type {
	ApiKeyResponse,
	CreateProjectRequest,
	GeneratedApiKeyResponse,
	GenerateKeyRequest,
	ProjectCreatedResponse,
	ProjectResponse,
	UpdateProjectRequest,
} from '@orderlymeds/measurements-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiFetch } from './ApiProvider';

// Query keys
export const projectKeys = {
	all: ['projects'] as const,
	detail: (id: string) => ['projects', id] as const,
	keys: (id: string) => ['projects', id, 'keys'] as const,
};

export function useProjects() {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: projectKeys.all,
		queryFn: () => apiFetch<ProjectResponse[]>('/api/admin/projects'),
		staleTime: 30_000,
	});
}

export function useProject(id: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: projectKeys.detail(id),
		queryFn: () => apiFetch<ProjectResponse>(`/api/admin/projects/${id}`),
		enabled: !!id,
		staleTime: 30_000,
	});
}

export function useProjectKeys(projectId: string, enabled = true) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: projectKeys.keys(projectId),
		queryFn: () => apiFetch<ApiKeyResponse[]>(`/api/admin/projects/${projectId}/keys`),
		enabled: enabled && !!projectId,
		staleTime: 30_000,
	});
}

export function useCreateProject() {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateProjectRequest) =>
			apiFetch<ProjectCreatedResponse>('/api/admin/projects', {
				method: 'POST',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: projectKeys.all });
		},
	});
}

export function useUpdateProject(id: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateProjectRequest) =>
			apiFetch<ProjectResponse>(`/api/admin/projects/${id}`, {
				method: 'PUT',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: projectKeys.all });
		},
	});
}

export function useArchiveProject(id: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () =>
			apiFetch<void>(`/api/admin/projects/${id}`, {
				method: 'DELETE',
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: projectKeys.all });
		},
	});
}

export function useGenerateKey(projectId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: GenerateKeyRequest) =>
			apiFetch<GeneratedApiKeyResponse>(`/api/admin/projects/${projectId}/keys`, {
				method: 'POST',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: projectKeys.keys(projectId) });
		},
	});
}

export function useDeactivateKey(projectId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (keyId: string) =>
			apiFetch<void>(`/api/admin/projects/${projectId}/keys/${keyId}`, {
				method: 'DELETE',
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: projectKeys.keys(projectId) });
		},
	});
}
